import React from 'react';
import DatabaseBrowser from './DatabaseBrowser';

/**
 * Complete Database Column Component
 * 
 * This component replicates the database column from the count creation page,
 * including the header, title, and the database browser functionality.
 * 
 * Features:
 * - Column header with icon and description
 * - Integrated DatabaseBrowser component
 * - Configurable styling and behavior
 * - Responsive design
 * 
 * @param {Object} props - Component props
 * @param {Array} props.inventoryData - Array of inventory items
 * @param {Array} props.currentItems - Currently selected items
 * @param {Function} props.onAddItem - Callback when item is added
 * @param {Function} props.onItemClick - Callback when item is clicked
 * @param {Object} props.config - Configuration options
 * @param {string} props.config.title - Column title (default: "Add Items from Database")
 * @param {string} props.config.subtitle - Column subtitle
 * @param {string} props.config.icon - Feather icon name (default: "database")
 * @param {boolean} props.config.showHeader - Show column header (default: true)
 * @param {Object} props.browserConfig - Configuration passed to DatabaseBrowser
 * @param {Object} props.styling - Custom styling options
 */
const DatabaseColumn = ({
  inventoryData = [],
  currentItems = [],
  onAddItem,
  onItemClick,
  onFilteredListChange,
  config = {},
  browserConfig = {},
  styling = {}
}) => {
  // Configuration with defaults
  const {
    title = "Add Items from Database",
    subtitle = "Search and add items from your inventory database",
    icon = "database",
    showHeader = true,
    headerExtra = null
  } = config;

  // Default styling
  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    ...styling.column
  };

  const headerStyle = {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    ...styling.header
  };

  const titleStyle = {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#5A4A42',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    ...styling.title
  };

  const subtitleStyle = {
    margin: '0.25rem 0 0 0',
    fontSize: '0.875rem',
    color: '#6c757d',
    ...styling.subtitle
  };

  const browserContainerStyle = {
    flex: 1,
    minHeight: 0, // Important for flex child to shrink
    ...styling.browserContainer
  };

  return (
    <div style={columnStyle}>
      {/* Column Header */}
      {showHeader && (
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h6 style={titleStyle}>
                <i className={`feather icon-${icon}`}></i>
                {title}
              </h6>
              {subtitle && (
                <p style={subtitleStyle}>
                  {subtitle}
                </p>
              )}
            </div>
            {headerExtra ? <div className="flex-shrink-0">{headerExtra}</div> : null}
          </div>
        </div>
      )}

      {/* Database Browser */}
      <div style={browserContainerStyle}>
        <DatabaseBrowser
          inventoryData={inventoryData}
          currentItems={currentItems}
          onAddItem={onAddItem}
          onItemClick={onItemClick}
          onFilteredListChange={onFilteredListChange}
          config={browserConfig}
          styling={styling.browser || {}}
        />
      </div>
    </div>
  );
};

export default DatabaseColumn;
